import { forwardRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** The look of a call control, for triggers that can't be a ControlButton. */
export const controlButtonClassName =
  "h-auto min-w-10 flex-col gap-1 rounded-md px-2 py-2 text-muted-foreground hover:text-foreground sm:min-w-14 sm:py-1.5 [&_svg:not([class*='size-'])]:size-5";

/**
 * A control's short visible caption. Phones show the icons alone, or the bar
 * no longer fits; the label stays as the accessible name and tooltip.
 */
export const ControlCaption = ({ children }) => (
  <span className="hidden text-[11px] leading-none font-medium sm:block">
    {children}
  </span>
);

/**
 * A call control after Cal.com's: the icon with a short `caption` under it
 * ("Mute", "Chat"), and the full `label` as tooltip and accessible name.
 * `off` paints it as a muted/disabled state (red for the microphone and
 * camera), `active` as switched on (raised hand, open panel).
 */
export const ControlButton = forwardRef(
  (
    {
      label,
      caption,
      off = false,
      active = false,
      className,
      children,
      ...props
    },
    ref
  ) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="ghost"
          aria-label={label}
          aria-pressed={active || undefined}
          className={cn(
            controlButtonClassName,
            off && "text-destructive hover:text-destructive",
            active && "bg-accent text-foreground",
            className
          )}
          {...props}
        >
          {children}
          {caption ? <ControlCaption>{caption}</ControlCaption> : null}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
);
ControlButton.displayName = "ControlButton";
